import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, filter, map } from 'rxjs/operators';

import { authLogout } from '../../core/auth/auth.actions';
import { LogService } from '../../core/log/log.service';
import { dialogClosedEvent } from '../../core/current-page/current-page.actions';
import { openDialog } from '../../shared/dialog/dn-dialog.actions';
import { openEmailConfirmDialog } from '../../shared/dialog/mail-confirm-dialog/mail-confirm-dialog.actions';
import { WallDialogComponent } from '../../shared/dialog/wall-dialog/wall-dialog.component';
import { distinctUntilReset } from '../../shared/util/operators';
import { getTemplateFromDescription } from '../template-ct/template-ct.model';
import {
	loadUserSoftSuccess,
	loadUserSuccess
} from '../user/user.actions';
import { EmailConfirmStatus, ListaGruppi, UserDTO } from '../user/user.model';
import { siteAccessGateClosed, siteAccessGateOpened } from './site-access.actions';
import { SiteAccessGateId } from './site-access.model';

// Dialog component names (as dispatched via openDialog and seen in
// dialogClosedEvent). Centralized here so gate-closure logic stays in sync
// if either class is renamed.
const WALL_DIALOG_NAME = 'WallDialogComponent';
const MAIL_CONFIRM_DIALOG_NAME = 'MailConfirmDialogComponent';

// Segments where we never open site-access gates — static / self-service
// pages the user must be able to reach even while in-session gates are
// pending.
const URL_SEGMENT_BLOCKLIST = new Set(['privacy', 'condizioni-utilizzo', 'settings']);

interface GatePlan {
	user: UserDTO;
	wallGroups: ListaGruppi[];
	needsMailConfirm: boolean;
	firstSegment: string;
	signature: string;
}

@Injectable({ providedIn: 'root' })
export class SiteAccessEffects {
	constructor(
		private actions$: Actions,
		private location: Location,
		private logService: LogService
	) {}

	// On every user load (hard or soft), compute which site-access gates must
	// open and dispatch them in order: wall first, mail-confirm second. The
	// actions are emitted from a single concatMap, so the dialog stack gets
	// them deterministically in that order — no delay(0) microtask trick
	// needed (the previous implementation used delay(0) to force mail-confirm
	// on top of wall because the two effects were independent).
	openGatesOnUserLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadUserSuccess, loadUserSoftSuccess),
			map((payload) => this.planGates(payload.utente)),
			filter(
				(plan) =>
					!URL_SEGMENT_BLOCKLIST.has(plan.firstSegment) &&
					(plan.wallGroups.length > 0 || plan.needsMailConfirm)
			),
			// Session-scoped dedupe: soft→hard transitions on the same user
			// with the same gate signature must not re-open. Reset on logout
			// so the next user starts fresh (the comparator memory otherwise
			// lives on this singleton effect for the whole app lifetime).
			distinctUntilReset(
				(a, b) => a.user.idAnagrafica === b.user.idAnagrafica && a.signature === b.signature,
				this.actions$.pipe(ofType(authLogout))
			),
			concatMap((plan) => {
				const actions: ReturnType<typeof siteAccessGateOpened | typeof openDialog | typeof openEmailConfirmDialog>[] = [];
				if (plan.wallGroups.length > 0) {
					actions.push(siteAccessGateOpened({ gate: 'wall' }));
					actions.push(
						openDialog({
							content: undefined,
							urlToDownload: undefined,
							urlWithContext: undefined,
							componentType: WallDialogComponent,
							componentName: WALL_DIALOG_NAME,
							data: { listaGruppi: plan.wallGroups }
						})
					);
				}
				if (plan.needsMailConfirm) {
					actions.push(siteAccessGateOpened({ gate: 'mail-confirm' }));
					actions.push(
						openEmailConfirmDialog({
							callbackUrl: this.location.path(true)
						})
					);
				}
				return actions;
			})
		)
	);

	// When the wall closes (user submitted form or dismissed), remove its
	// gate. `dialogClosedEvent` is dispatched from dn-dialog.effects.ts's
	// afterClosed subscription — one listener covers both the submit path
	// (wall-dialog#submitForm → updateUser → closeDialog) and the X-dismiss
	// path.
	wallClosed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(dialogClosedEvent),
			filter(({ componentName }) => componentName === WALL_DIALOG_NAME),
			map(() => siteAccessGateClosed({ gate: 'wall' }))
		)
	);

	// Same for mail-confirm. Covers the free-banner X-dismiss (which fires
	// dismissEmailConfirmationSuccess → closeDialog → afterClosed → this
	// event) and the locking-banner successful-activation path (activation
	// closes the dialog; same terminal event).
	mailConfirmClosed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(dialogClosedEvent),
			filter(({ componentName }) => componentName === MAIL_CONFIRM_DIALOG_NAME),
			map(() => siteAccessGateClosed({ gate: 'mail-confirm' }))
		)
	);

	/**
	 * Compute the set of gates this user-load should open.
	 *
	 * URL read uses `Location.path(true)` rather than `Router.url` because on
	 * direct landings (refresh on a deep URL) `loadUserSuccess` can race
	 * ahead of the first `ROUTER_NAVIGATED` action — `Router.url` would
	 * still be `/` at this point. `Location` reads from `PlatformLocation`
	 * which reflects the real browser URL and is SSR-safe.
	 */
	private planGates(user: UserDTO): GatePlan {
		const firstSegment = this.location.path(true).split('?')[0].split('/')[1] ?? '';

		// Issue #155: a real user with no email on file (typical after a soft
		// login from an external token). We cannot ask them to *confirm* a blank
		// address, so suppress the mail-confirm dialog and instead force a
		// blocking, email-only wall that lets them enter one. `username_password`
		// maps to ListaGruppiEnum.Credentials → editable email field, no other
		// section; `mandatory: true` makes the wall non-dismissable.
		const isRealUser = user.idAnagrafica > 0;
		const hasEmail = !!user.email && user.email.trim().length > 0;
		if (isRealUser && !hasEmail) {
			const emailWallGroups: ListaGruppi[] = [
				{ group: 'username_password', mandatory: true, message: '', showGroup: true }
			];
			const emailSignature = this.signatureOf(
				emailWallGroups,
				false,
				user.emailConfirmationStatus
			);
			return {
				user,
				wallGroups: emailWallGroups,
				needsMailConfirm: false,
				firstSegment,
				signature: emailSignature
			};
		}

		const wallGroups: ListaGruppi[] = user.listaGruppi.filter((g) => g.showGroup);
		const extendedProfile = getTemplateFromDescription(firstSegment)?.extendedProfile;
		if (extendedProfile) {
			user.listaGruppi
				.filter((g) => g.group === extendedProfile)
				.forEach((g) => wallGroups.push(g));
		}

		const needsMailConfirm =
			user.emailConfirmationStatus === EmailConfirmStatus.ShowFreeBanner ||
			user.emailConfirmationStatus === EmailConfirmStatus.ShowLockingBanner;

		const signature = this.signatureOf(wallGroups, needsMailConfirm, user.emailConfirmationStatus);

		return { user, wallGroups, needsMailConfirm, firstSegment, signature };
	}

	/**
	 * Stable signature for the session-scoped dedupe comparator. Combines
	 * wall-group identifiers (sorted) with the mail-confirm status so a
	 * change in either gate re-triggers the effect, while an identical
	 * subsequent loadUser (e.g. soft→hard) is dropped.
	 */
	private signatureOf(
		wallGroups: ListaGruppi[],
		needsMailConfirm: boolean,
		emailStatus: EmailConfirmStatus
	): string {
		const walls = wallGroups
			.map((g) => g.group)
			.sort()
			.join(',');
		return `wall:[${walls}]|mail:${needsMailConfirm ? emailStatus : 'no'}`;
	}
}
